import { difference } from 'lodash';
import React, { FC, ReactNode } from 'react';
import { AuthenticatedState, AuthService } from '@trp-ta-nitro/auth-browser-js';
import { useAuthService, useAuthState } from './hooks';
import { ElementOrRender, elementOrRenderComponent } from './props';

type Entitlement = string;
type EntitlementSet = Entitlement[];

// The entitlements can be configured as:
//   - undefined - No entitlements are required.
//   - string - That one entitlement is required.
//   - string[] - All of the entitlements in the array are required.
//   - string[][] - Any set of individual entitlements is required.
export type Entitlements = Entitlement | EntitlementSet | EntitlementSet[] | undefined;

/**
 * Returns the proper set of entitlements, internal or external, based on the type of the auth
 * service.
 *
 * @param authService
 * @param externalEntitlements
 * @param internalEntitlements
 */
function entitlementsByType(
  authService: AuthService,
  externalEntitlements: Entitlements,
  internalEntitlements: Entitlements,
) {
  switch (authService.type()) {
    case 'external':
      return externalEntitlements;
    case 'internal':
      return internalEntitlements;
    default:
      throw new Error(
        `Unknown auth type '${authService.type()}' when choosing entitlements.`,
      );
  }
}

/**
 * Takes the full entitlements spec and handles the case of a single entitlement by converting it
 * to an array.
 *
 * Also handles the case of no entitlements being defined.
 *
 * @param entitlements
 */
function coerceEntitlement(
  entitlements: Entitlements,
): EntitlementSet | EntitlementSet[] {
  if (!entitlements) {
    return [];
  }
  if (typeof entitlements === 'string') {
    return [entitlements];
  }
  return entitlements;
}

/**
 * Returns true if the passed argument is an EntitlementSet, meaning an array of strings.
 *
 * @param x Any variable that might possibly be an EntitlementSet.
 */
function isEntitlementSet(x: any): x is EntitlementSet {
  if (!Array.isArray(x)) {
    return false;
  }
  return x.every((entitlement) => typeof entitlement === 'string');
}

/**
 * Takes a full entitlements specification and returns a consistent array of EntitlementSet for
 * comparison.
 *
 * @param entitlements
 */
function normalizeEntitlements(entitlements: Entitlements): EntitlementSet[] {
  const e = coerceEntitlement(entitlements);
  if (isEntitlementSet(e)) {
    return [e];
  }
  return e;
}

/**
 * Returns true if the passed `entitlements` are within the `requiredEntitlements`, false otherwise.
 *
 * @param entitlements
 * @param requiredEntitlements
 */
function hasEntitlements(
  entitlements: EntitlementSet | undefined,
  requiredEntitlements: EntitlementSet,
) {
  if (!entitlements) {
    return false;
  }
  return difference(requiredEntitlements, entitlements).length === 0;
}

/**
 * Returns true if either (1) there were no entitlements to check (the user has all zero required
 * entitlements) or (2) entitlements were configured and the user has all of them.
 *
 * @param authState
 * @param targetEntitlements
 */
function isAuthorized(authState: AuthenticatedState, targetEntitlements: Entitlements) {
  if (!targetEntitlements || targetEntitlements.length === 0) {
    // the gate is open if no target entitlements are configured, the developer that uses the AuthGate
    // is specifically saying the user doesn't need any specific entitlements to get through, this
    // facilitates the use case of an AuthGate that is used solely for an authentication check
    return true;
  }

  return normalizeEntitlements(targetEntitlements).some((entitlements) =>
    hasEntitlements(authState.entitlements, entitlements),
  );
}

export interface Props {
  children?: ReactNode;
  // entitlements to enforce if the current auth type is external, see the Entitlements type for a description
  externalEntitlements?: Entitlements;
  // entitlements to enforce if the current auth type is external, see the Entitlements type for a description
  internalEntitlements?: Entitlements;
  // a component type or function to call to render when the auth state is loading
  loading?: ElementOrRender;
  // a component type or function to call to render when the user is not logged in
  unauthenticated?: ElementOrRender;
  // a component type or function to call to render when the user is logged in but does not
  // have the required entitlements
  unauthorized?: ElementOrRender;
}

/**
 * A component that will only render its children if the user is authenticated (and optionally
 * authorized). This must be nested beneath an <AuthServiceProvider /> as this uses its
 * configuration.
 *
 * Optionally, the `externalEntitlements` and `internalEntitlements` properties can be passed a set
 * of properties to check (an array of strings). If these properties are configured, the user must
 * have these entitlements before the children will render.
 *
 * If the auth state is still loading, the component or render function of the `loading` property
 * will be used. If this is not configured, nothing will be rendered.
 *
 * If the user is unauthenticated (not logged in), the component or render function of the
 * `unauthenticated` property will be used. If this is not configured, nothing will be rendered.
 *
 * If user is authenticated (logged in) but does not have all required entitlements, the component
 * or render function of the `unauthorized` property will be used. If this is not configured,
 * nothing will be rendered.
 *
 * @param props
 * @constructor
 */
const AuthGate: FC<Props> = (props) => {
  const { children, externalEntitlements, internalEntitlements } = props;
  const authService = useAuthService();
  const authState = useAuthState();

  if (!authService || !authState) {
    return elementOrRenderComponent(props.loading);
  }

  if (!authState.authenticated) {
    return elementOrRenderComponent(props.unauthenticated);
  }

  const entitlements = entitlementsByType(
    authService,
    externalEntitlements,
    internalEntitlements,
  );

  if (!isAuthorized(authState, entitlements)) {
    return elementOrRenderComponent(props.unauthorized);
  }

  return <>{children}</>;
};

export default AuthGate;
